import {useEffect, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {observer} from 'mobx-react';
import styled from 'styled-components';
import AppStore from '../../app.store';
import {media} from '../../utils/variables';

const BANNER_DESKTOP_HEIGHT = 42;
const BANNER_COMPACT_HEIGHT = 64;
const NOTICE_DAYS = 10;

const Banner = styled.div<{
    $tone: 'warning' | 'danger';
}>`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    box-sizing: border-box;
    z-index: 5;
    width: 100vw;
    min-height: ${BANNER_DESKTOP_HEIGHT}px;
    padding: 8px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    border-top: 1px solid ${({$tone}) => $tone === 'danger' ? '#f1b4b4' : '#f2d483'};
    border-bottom: 1px solid ${({$tone}) => $tone === 'danger' ? '#e68a8a' : '#e2bd4f'};
    background: ${({$tone}) => $tone === 'danger' ? '#fff1f0' : '#fff8df'};
    color: ${({$tone}) => $tone === 'danger' ? '#a8071a' : '#7a4d00'};
    font-size: 14px;
    font-weight: 600;
    transition: 250ms;

    ${media.mobile} {
        min-height: ${BANNER_COMPACT_HEIGHT}px;
        padding: 8px 15px;
        align-items: flex-start;
        flex-direction: column;
        gap: 6px;
    }

    ${media.tablet} {
        min-height: ${BANNER_COMPACT_HEIGHT}px;
        padding: 8px 15px;
    }
`;

const TextBlock = styled.div`
    line-height: 1.35;
    min-width: 0;
    padding-left: 0;
`;

const PayButton = styled.button<{
    $tone: 'warning' | 'danger';
}>`
    height: 28px;
    padding: 0 12px;
    border: none;
    border-radius: 6px;
    background: ${({$tone}) => $tone === 'danger' ? '#cf1322' : '#d48806'};
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        opacity: 0.9;
    }
`;

const BillingNotice = observer(() => {
    const navigate = useNavigate();
    const {billingSummary, devices} = AppStore;

    const notice = useMemo(() => {
        const status = billingSummary?.status || '';
        const daysRemaining = Number(billingSummary?.usage?.days_remaining || 0);
        const isReadOnly = Boolean(
            billingSummary?.blocked ||
            status === 'expired' ||
            status === 'no_subscription'
        );

        if (status === 'system') {
            return null;
        }

        if (isReadOnly) {
            return {
                tone: 'danger' as const,
                text: status === 'no_subscription'
                    ? 'Подписка не оплачена. Доступен только просмотр данных до оплаты подписки.'
                    : 'Срок подписки истек. Доступен только просмотр данных до оплаты подписки.',
            };
        }

        if (status === 'active' && daysRemaining > 0 && daysRemaining <= NOTICE_DAYS) {
            return {
                tone: 'warning' as const,
                text: `Осталось ${daysRemaining} дн. до окончания подписки. После окончания доступ будет только на чтение.`,
            };
        }

        return null;
    }, [billingSummary]);

    const compact = devices.mobile || devices.tablet;
    const visible = Boolean(notice);

    useEffect(() => {
        document.documentElement.style.setProperty(
            '--billing-banner-height',
            visible ? `${compact ? BANNER_COMPACT_HEIGHT : BANNER_DESKTOP_HEIGHT}px` : '0px'
        );

        return () => {
            document.documentElement.style.setProperty('--billing-banner-height', '0px');
        };
    }, [compact, visible]);

    if (!notice) {
        return null;
    }

    return (
        <Banner $tone={notice.tone}>
            <TextBlock>{notice.text}</TextBlock>
            <PayButton
                type="button"
                $tone={notice.tone}
                onClick={() => navigate('/subscription')}
            >
                Оплатить
            </PayButton>
        </Banner>
    );
});

export default BillingNotice;
